import React, { useMemo, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import type { RootState } from "../../store";
import { useCoreValues } from "../../services/coreValueService";

interface ValueCard {
  id: string;
  icon: React.ReactNode;
  title: string;
  description: string;
  color: string;
  gradient: string;
}

interface CoreValuesProps {
  title?: string;
  subtitle?: string;
  values?: ValueCard[];
  showLearnMore?: boolean;
  onLearnMore?: (value: ValueCard) => void;
}

const CoreValues: React.FC<CoreValuesProps> = ({
  title,
  subtitle,
  values,
  showLearnMore = false,
  onLearnMore,
}) => {
  const { t } = useTranslation();
  const [isLoaded, setIsLoaded] = useState(false);
  const { currentLanguage } = useSelector((state: RootState) => state.language);
  const { data: coreValuesData } = useCoreValues({
    locale: currentLanguage,
  });

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  // Default values with translations and enhanced styling
  const defaultValues = useMemo(
    () => [
      {
        id: "community",
        icon: (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="48"
            height="48"
            fill="currentColor"
            viewBox="0 0 256 256"
            className="text-white"
          >
            <path d="M244.8,150.4a8,8,0,0,1-11.2-1.6A51.6,51.6,0,0,0,192,128a8,8,0,0,1-7.37-4.89,8,8,0,0,1,0-6.22A8,8,0,0,1,192,112a24,24,0,1,0-23.24-30,8,8,0,1,1-15.5-4A40,40,0,1,1,219,117.51a67.94,67.94,0,0,1,27.43,21.68A8,8,0,0,1,244.8,150.4ZM190.92,212a8,8,0,0,1-13.84,8,57,57,0,0,0-98.16,0,8,8,0,1,1-13.84-8,72.06,72.06,0,0,1,33.74-29.92,48,48,0,1,1,58.36,0A72.06,72.06,0,0,1,190.92,212ZM128,176a32,32,0,1,0-32-32A32,32,0,0,0,128,176ZM72,120a8,8,0,0,0-8-8A24,24,0,1,1,87.24,82a8,8,0,0,0,15.5-4A40,40,0,1,0,37,117.51,67.94,67.94,0,0,0,9.6,139.19a8,8,0,0,0,12.8,9.61A51.6,51.6,0,0,1,64,128,8,8,0,0,0,72,120Z" />
          </svg>
        ),
        title: t("values.community.title", "Community"),
        description: t(
          "values.community.description",
          "Building a strong, inclusive community where every voice matters and collaboration drives progress."
        ),
        color: "text-emerald-600 dark:text-emerald-400",
        gradient: "from-emerald-500 to-stone-600",
      },
      {
        icon: (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="48"
            height="48"
            fill="currentColor"
            viewBox="0 0 256 256"
            className="text-white"
          >
            <path d="M247.31,124.76c-.35-.79-8.82-19.58-27.65-38.41C194.57,61.26,162.88,48,128,48S61.43,61.26,36.34,86.35C17.51,105.18,9,124,8.69,124.76a8,8,0,0,0,0,6.5c.35.79,8.82,19.57,27.65,38.4C61.43,194.74,93.12,208,128,208s66.57-13.26,91.66-38.34c18.83-18.83,27.3-37.61,27.65-38.4A8,8,0,0,0,247.31,124.76ZM128,192c-30.78,0-57.67-11.19-79.93-33.25A133.47,133.47,0,0,1,25,128,133.33,133.33,0,0,1,48.07,97.25C70.33,75.19,97.22,64,128,64s57.67,11.19,79.93,33.25A133.46,133.46,0,0,1,231.05,128C223.84,141.46,192.43,192,128,192Zm0-112a48,48,0,1,0,48,48A48.05,48.05,0,0,0,128,80Zm0,80a32,32,0,1,1,32-32A32,32,0,0,1,128,160Z" />
          </svg>
        ),
        id: "transparency",
        title: t("values.transparency.title", "Transparency"),
        description: t(
          "values.transparency.description",
          "Open and honest communication in all our activities and decisions, ensuring trust and accountability."
        ),
        color: "text-stone-600 dark:text-stone-400",
        gradient: "from-stone-500 to-emerald-600",
      },
      {
        icon: (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="48"
            height="48"
            fill="currentColor"
            viewBox="0 0 256 256"
            className="text-white"
          >
            <path d="M119.76,217.94A8,8,0,0,1,112,224a8.13,8.13,0,0,1-2-.24l-32-8a8,8,0,0,1-2.5-1.11l-24-16a8,8,0,1,1,8.88-13.31l22.84,15.23,30.66,7.67A8,8,0,0,1,119.76,217.94Zm132.69-96.46a15.89,15.89,0,0,1-8,9.25l-23.68,11.84-55.08,55.09a8,8,0,0,1-7.6,2.1l-64-16a8.06,8.06,0,0,1-2.71-1.25L35.86,142.87,11.58,130.73a16,16,0,0,1-7.16-21.46L29.27,59.58h0a16,16,0,0,1,21.46-7.16l22.06,11,53-15.14a8,8,0,0,1,4.4,0l53,15.14,22.06-11a16,16,0,0,1,21.46,7.16l24.85,49.69A15.9,15.9,0,0,1,252.45,121.48Zm-46.18,12.94L179.06,80H147.24L104,122c12.66,8.09,32.51,10.32,50.32-7.63a8,8,0,0,1,10.68-.61l34.41,27.57Zm-187.54-18,17.69,8.85L61.27,75.58,43.58,66.73ZM188,152.66l-27.71-22.19c-19.54,16-44.35,18.11-64.91,5a16,16,0,0,1-2.72-24.82.6.6,0,0,1,.08-.08L137.6,67.06,128,64.32,77.58,78.73,50.21,133.46l49.2,35.15,58.14,14.53Zm49.24-36.24L212.42,66.73l-17.69,8.85,24.85,49.69Z" />
          </svg>
        ),
        id: "impact",
        title: t("values.impact.title", "Impact"),
        description: t(
          "values.impact.description",
          "Delivering meaningful and sustainable improvements that create lasting positive change in our community."
        ),
        color: "text-emerald-600 dark:text-emerald-400",
        gradient: "from-emerald-500 to-stone-600",
      },
    ],
    [t]
  );

  // Transform API data to component format
  const apiValues = useMemo(() => {
    if (!coreValuesData?.core_values) return null;

    return coreValuesData.core_values
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .slice(0, 4)
      .map((coreValue) => ({
        id: coreValue.id?.toString() || String(coreValue.id),
        icon: coreValue.icon ? (
          <i className={coreValue.icon} style={{ fontSize: "48px" }}></i>
        ) : (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="48"
            height="48"
            fill="currentColor"
            viewBox="0 0 256 256"
            className="text-white"
          >
            <path d="M128,24A104,104,0,1,0,232,128,104.11,104.11,0,0,0,128,24Zm0,192a88,88,0,1,1,88-88A88.1,88.1,0,0,1,128,216Zm16-40a8,8,0,0,1-8,8,16,16,0,0,1-16-16V128a8,8,0,0,0-8-8,16,16,0,0,1,0-32,8,8,0,0,0,8-8,16,16,0,0,1,16-16,8,8,0,0,1,8,8V168A8,8,0,0,1,144,176Z" />
          </svg>
        ),
        // Use title and description directly from API response
        title: coreValue.title || `Core Value ${coreValue.id}`,
        description: coreValue.description || "",
        color: coreValue.color || "text-emerald-600 dark:text-emerald-400",
        gradient: coreValue.gradient || "from-emerald-500 to-stone-600",
      }));
  }, [coreValuesData]);

  // Use API data if available, otherwise fall back to defaults
  const displayValues = values || apiValues || defaultValues;

  return (
    <section className="relative py-24 px-4 sm:px-6 lg:px-8 overflow-hidden bg-gradient-to-br from-white via-emerald-50/50 to-stone-100/50 dark:from-gray-900 dark:via-emerald-900/10 dark:to-stone-900/10">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-96 h-96 bg-emerald-200/20 dark:bg-emerald-900/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-stone-200/20 dark:bg-stone-900/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isLoaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={
              isLoaded ? { scale: 1, opacity: 1 } : { scale: 0.9, opacity: 0 }
            }
            transition={{ duration: 0.6, delay: 0.2 }}
            className="inline-block mb-6"
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm text-emerald-800 dark:text-emerald-300 rounded-full text-sm font-semibold border border-emerald-200 dark:border-emerald-800 shadow-lg">
              <span className="text-lg">💎</span>
              <span>{t("values.badge", "Our Foundation")}</span>
            </span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={isLoaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold text-gray-900 dark:text-white mb-6 leading-tight tracking-tight"
          >
            {title || t("values.title", "Our Core Values")}
            <span className="block mt-2 bg-gradient-to-r from-emerald-600 via-stone-500 to-emerald-700 bg-clip-text text-transparent">
              {t("values.titleAccent", "That Drive Us")}
            </span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={isLoaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="text-lg md:text-xl text-gray-600 dark:text-gray-300 leading-relaxed max-w-3xl mx-auto"
          >
            {subtitle ||
              t(
                "values.subtitle",
                "The principles that guide everything we do and shape our community's future."
              )}
          </motion.p>
        </motion.div>

        {/* Values Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8 lg:gap-10">
          {displayValues.map((value, index) => (
            <motion.div
              key={value.id}
              initial={{ opacity: 0, y: 50 }}
              animate={isLoaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
              transition={{ duration: 0.6, delay: 0.2 + index * 0.15 }}
              whileHover={{ y: -12, scale: 1.03 }}
              className="group relative"
            >
              {/* Floating gradient effect */}
              <div
                className={`absolute -inset-1 bg-gradient-to-r ${value.gradient} rounded-3xl opacity-0 group-hover:opacity-20 blur-xl transition-opacity duration-500`}
              ></div>

              {/* Card */}
              <div className="relative bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-3xl p-8 lg:p-10 shadow-xl hover:shadow-2xl transition-all duration-500 border border-gray-100/50 dark:border-gray-700/50 h-full">
                {/* Icon with gradient background */}
                <motion.div
                  whileHover={{ rotate: 5, scale: 1.1 }}
                  className={`inline-flex items-center justify-center w-20 h-20 lg:w-24 lg:h-24 rounded-2xl bg-gradient-to-br ${value.gradient} mb-6 shadow-xl group-hover:shadow-2xl transition-all duration-300`}
                >
                  {value.icon}
                </motion.div>

                {/* Content */}
                <h3 className="text-2xl lg:text-3xl font-extrabold text-gray-900 dark:text-white mb-4 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-emerald-600 group-hover:to-stone-600 transition-all duration-300">
                  {value.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-6 text-base lg:text-lg">
                  {value.description}
                </p>

                {/* Learn More Button */}
                {showLearnMore && (
                  <motion.button
                    whileHover={{ scale: 1.05, x: 4 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => onLearnMore?.(value)}
                    className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300 transition-colors group/btn"
                  >
                    {t("values.learnMore", "Learn More")}
                    <svg
                      className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform duration-200"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </motion.button>
                )}

                {/* Decorative gradient circle */}
                <div
                  className={`absolute -bottom-8 -right-8 w-32 h-32 bg-gradient-to-br ${value.gradient} opacity-10 rounded-full blur-2xl group-hover:opacity-20 transition-opacity duration-500`}
                ></div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bottom CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isLoaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="mt-20 text-center"
        >
          <div className="bg-white/60 dark:bg-gray-800/60 backdrop-blur-md rounded-3xl p-8 lg:p-12 border border-gray-200/50 dark:border-gray-700/50 shadow-xl">
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={
                isLoaded
                  ? { scale: 1, opacity: 1 }
                  : { scale: 0.95, opacity: 0 }
              }
              transition={{ duration: 0.6, delay: 1 }}
              className="mb-6"
            >
              <span className="text-5xl">✨</span>
            </motion.div>
            <h3 className="text-2xl lg:text-3xl font-extrabold text-gray-900 dark:text-white mb-4">
              {t("values.cta.title", "Ready to Make a Difference?")}
            </h3>
            <p className="text-lg text-gray-600 dark:text-gray-300 mb-8 max-w-2xl mx-auto">
              {t(
                "values.cta.description",
                "Join us in building a stronger community. Your involvement matters and creates lasting impact."
              )}
            </p>
            <motion.button
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-emerald-600 to-stone-600 hover:from-emerald-700 hover:to-stone-700 text-white rounded-2xl font-bold text-lg shadow-xl hover:shadow-2xl transition-all duration-300"
            >
              {t("values.cta.button", "Get Involved")}
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 7l5 5m0 0l-5 5m5-5H6"
                />
              </svg>
            </motion.button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default CoreValues;
